import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  computed,
  Directive,
  inject,
  Injector,
  OnDestroy,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  CreateUpdateThirdPartyDocDto,
  CustomerQuoteContactDetailsDto,
  DayBookService,
  ThirdPartyDocDefaultValueDto,
  ThirdPartyDocDto,
  VatValidationResultDto,
} from '@digiwall/goliath-documents/proxy';
import { BaseDocDetail } from '../base-docs/detail/base-doc-detail';
import { DocSaveRequestContext } from '../base-docs/detail/doc-save-request-context';
import { SendTextTemplateOptions } from '../base-docs/detail/send-text-template-options';
import { IBaseDocService } from '../base-docs/detail/i-base-doc-service';
import {
  AccountingCustomerOutstandingResponseDto,
  AddressDto,
  CompanyWebInfoDto,
  ContactDto,
  CustomerDto,
  CustomerService,
  LeadService,
  SupplierDto,
  SupplierService,
  ThirdPartyDocCountDto,
  ThirdPartyDto,
  ThirdPartyLookupDto,
  ThirdPartyWarningMessageDto,
  WorkingSiteService,
} from '@digiwall/goliath-contacts/proxy';
import {
  BehaviorSubject,
  concatMap,
  first,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  Subject,
  tap,
  throwError,
} from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfigStateService,
  ListResultDto,
  LocalizationService,
  Rest,
  SubscriptionService,
} from '@abp/ng.core';
import { FormBuilder, Validators } from '@angular/forms';
import { ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
  ClassificationTypeService,
  DataProvider,
  DwActiveLookupRequestDto,
  ThirdPartyRole,
} from '@digiwall/goliath-core/proxy';
import { DocTemplateService } from '../base-docs/detail/doc-template-selection/doc-template.service';
import {
  DeactivatableComponentService,
  LanguageStore,
  LoadingService,
  NewGuid,
  NewId,
} from '@digiwall/core';
import { ContactType } from '@digiwall/goliath-contacts';
import { DocVatCodeComputationDialogComponent } from '../base-docs/doc-vat-code-computations/doc-vat-code-computation-dialog.component';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { CurrentCompanyStore } from '@digiwall/goliath-core';
import { DropdownAction } from '@digiwall/ui-kit';
import { TextTemplateDto } from '@digiwall/core/proxy';
import { BaseThirdPartyDocLineUpdatedDto } from '../../components';
import { LinePriceDetails } from '../../components/lines-editor/base-doc-line-editor/base-doc-line-editor';

export interface FieldMapping {
  formField: string;
  customerField: keyof CustomerDto | keyof SupplierDto;
  transform?: (value: any) => any;
}

export interface IThirdPartyService {
  get: (id: string, config?: Partial<Rest.Config>) => Observable<ThirdPartyDto>;
  getDocLinkedCount: (
    thirdPartyId: string,
    config?: Partial<Rest.Config>,
  ) => Observable<ThirdPartyDocCountDto>;
  contacts: (
    thirdPartyId: string,
    typeId?: string,
    onlyActive?: boolean,
    config?: Partial<Rest.Config>,
  ) => Observable<ContactDto[]>;
  addressList: (thirdPartyId: string, config?: Partial<Rest.Config>) => Observable<AddressDto[]>;
  getContact: (
    thirdPartyId: string,
    contactId: string,
    config?: Partial<Rest.Config>,
  ) => Observable<ContactDto>;
  getLookUp: (id: string, config?: Partial<Rest.Config>) => Observable<ThirdPartyLookupDto>;
  updateCompanyWebFinancialData: (
    id: string,
    vatNumber: string,
    config?: Partial<Rest.Config>,
  ) => Observable<CompanyWebInfoDto>;
  getLookupList: (
    lookupRequest?: DwActiveLookupRequestDto<string>,
    config?: Partial<Rest.Config>,
  ) => Observable<ListResultDto<ThirdPartyLookupDto>>;
  getThirdPartyWarningMessage: (
    thirdPartyId: string,
    config?: Partial<Rest.Config>,
  ) => Observable<ThirdPartyWarningMessageDto>;
}

export interface IThirdPartyDocService<
  TDocDTO extends ThirdPartyDocDto,
  TCreateUpdateDto extends CreateUpdateThirdPartyDocDto,
  TDefaultValueDto extends ThirdPartyDocDefaultValueDto,
> extends IBaseDocService<TDocDTO, TCreateUpdateDto, TDefaultValueDto> {
  getContactDetails(
    thirdPartyId: string,
    deliveryAddressId?: string,
    contactId?: string,
  ): Observable<CustomerQuoteContactDetailsDto>;
  validateVatSystem(docId: string, thirdpartyId: string): Observable<VatValidationResultDto>;
  updateAndComputeVATCodes(
    docId: string,
    input: TCreateUpdateDto,
    evaluateArticlePrices: boolean,
  ): Observable<TDocDTO>;

  getSalesPersonDefaultValue(config?: Partial<Rest.Config>): Observable<string>;
}

@Directive()
export abstract class BaseThirdPartyDocDetail<
    TDocService extends IThirdPartyDocService<TDocDto, TCreateUpdateDto, TDefaultValueDto>,
    TDocDto extends ThirdPartyDocDto,
    TCreateUpdateDto extends CreateUpdateThirdPartyDocDto,
    TDefaultValueDto extends ThirdPartyDocDefaultValueDto,
    TThirdPartyService extends IThirdPartyService,
  >
  extends BaseDocDetail<TDocService, TDocDto, TCreateUpdateDto, TDefaultValueDto>
  implements OnDestroy
{
  protected linePricingDetails: LinePriceDetails;

  protected modalService = inject(NgbModal);
  protected loadingService = inject(LoadingService);
  protected currentCompanyStore = inject(CurrentCompanyStore);

  accountingCustomerOutstandingDto: AccountingCustomerOutstandingResponseDto;
  creditLimit: number = 0;
  destroy$ = new Subject<void>();
  baseActionsThirdPartyDoc: Signal<DropdownAction[]> = computed(() => {
    return this.baseActions().concat({
      label: 'Contact::UpdateCompanyWebData',
      icon: ' digi-out-synchronization',
      action: () => {
        this.loadingService.interceptNextRequest();
        this.thirdPartyService
          .updateCompanyWebFinancialData(this.entity.thirdPartyId, this.entity.thirdPartyVatNumber)
          .pipe(first())
          .subscribe(response => {
            this.entity = {
              ...this.entity,
              thirdPartyFinancialScore: response.financialScore,
              thirdPartyFinancialScoreImage: response.financialScoreImage,
              thirdPartyFinancialDataLastUpdateDate: response.financialDataLastUpdateDate,
              thirdPartyFinancialStatus: response.financialStatus,
            };

            this.toastService.success('Contact::CompanyWebDataLoadedSuccessfully');
          });
      },
      visible: computed(
        () =>
          this.currentCompanyStore.supplierDataConfiguration().dataSupplierId != DataProvider.None,
      ),
    });
  });

  protected thirdPartyService: TThirdPartyService;
  protected thirdPartyService$ = new BehaviorSubject<TThirdPartyService | null>(null);
  private thirdPartyCache = new Map<string, Observable<CustomerDto | SupplierDto>>();

  protected leadService = inject(LeadService);
  protected customerService = inject(CustomerService);
  protected workingSiteService = inject(WorkingSiteService);
  private languageStore = inject(LanguageStore);

  protected thirdPartyDeliveryAddress: WritableSignal<AddressDto> = signal(null);
  protected currentSalesPersonId: string;
  protected languageCode: string;

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    docService: TDocService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    classificationTypeService: ClassificationTypeService,
    quoteTemplateService: DocTemplateService,
    injector: Injector,
    config: ConfigStateService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
    thirdPartyService: TThirdPartyService,
  ) {
    super(
      breadcrumbService,
      dayBookService,
      classificationTypeService,
      formBuilder,
      localizationService,
      docService,
      toastService,
      router,
      subscriptionService,
      quoteTemplateService,
      injector,
      config,
      confirmationService,
      deactivableComponentService,
      route,
    );

    this.thirdPartyService = thirdPartyService;
    if (this.thirdPartyService != null) this.thirdPartyService$.next(this.thirdPartyService);

    this.currentCompanyStore.ensureLoaded().subscribeOnce(() => this.setBaseAction());
  }

  setBaseAction() {
    this.baseActionsThirdPartyDoc = computed(() => {
      return this.baseActions().concat({
        label: 'Contact::UpdateCompanyWebData',
        icon: ' digi-out-synchronization',
        action: () => {
          this.loadingService.interceptNextRequest();
          this.thirdPartyService
            .updateCompanyWebFinancialData(
              this.entity.thirdPartyId,
              this.entity.thirdPartyVatNumber,
            )
            .pipe(first())
            .subscribe(response => {
              this.entity = {
                ...this.entity,
                thirdPartyFinancialScore: response.financialScore,
                thirdPartyFinancialScoreImage: response.financialScoreImage,
                thirdPartyFinancialDataLastUpdateDate: response.financialDataLastUpdateDate,
                thirdPartyFinancialStatus: response.financialStatus,
              };

              this.toastService.success('Contact::CompanyWebDataLoadedSuccessfully');
            });
        },
        visible: computed(
          () =>
            this.currentCompanyStore.supplierDataConfiguration().dataSupplierId !=
            DataProvider.None,
        ),
      });
    });
  }

  override get formModel() {
    return Object.assign({}, super.formModel, {
      thirdPartyId: [null, Validators.required],
      retainBackOrders: [false],
      litigation: [false],
      litigationReason: [null],
      isBlocked: [false],
      blockedReason: [null],
      deliveryVehicleId: [null],
      deliveryInfo: [''],
      reference: [null],
      thirdPartyContactId: [null],
      paymentTermId: [null],
      invoiceTermId: [null],
      paymentMethodId: [null],
      financialDiscount: [null, Validators.min(0.01)],
      financialDiscountDays: [null, Validators.min(0)],
    });
  }

  protected override sendTextTemplate(
    textTemplate: TextTemplateDto,
    options: SendTextTemplateOptions,
  ): void {
    if (!this.entity.preferredPrintTemplateId) this.entity.preferredPrintTemplateId = NewGuid;

    const docPrintTemplateId =
      (textTemplate.extraProperties?.['PrintTemplateId'] as unknown as string) ??
      this.entity.preferredPrintTemplateId;

    this.subscriptionService.addOne(
      this.docService.getPrintTemplateId(docPrintTemplateId, this.entity.languageId),
      response => {
        this.printTemplateId = response?.id;

        if (this.pdfAttachment == null) {
          this.docService
            .getPdfAttachment(this.docRole, this.entity.id, this.printTemplateId)
            .pipe(
              tap(response => {
                this.pdfAttachment = response;
                super.sendTextTemplate(textTemplate, options);
              }),
            )
            .subscribe();
        } else {
          super.sendTextTemplate(textTemplate, options);
        }
      },
    );
  }

  protected override fetchAccountingData(docId: string, thirdPartyId: string): void {
    super.fetchAccountingData(docId, thirdPartyId);

    if (this.thirdParty == null) {
      this.thirdPartyService
        .get(thirdPartyId)
        .pipe(
          tap(res => {
            this.thirdParty = res;
          }),
        )
        .subscribe();
    }

    if (this.thirdPartyService instanceof CustomerService) {
      const service = this.thirdPartyService as CustomerService;
      service
        .getAccountingCustomerOutstanding(thirdPartyId)
        .pipe(first())
        .subscribe(res => (this.accountingCustomerOutstandingDto = res));

      (this.thirdPartyService as CustomerService)
        .getCreditLimit(thirdPartyId)
        .pipe(first())
        .subscribe(res => (this.creditLimit = res));
    }
  }

  protected override refreshAfterLineUpdate(params: Partial<BaseThirdPartyDocLineUpdatedDto>) {
    super.refreshAfterLineUpdate(params);
    if (params.margin != null) this.entity.margin = params.margin;
    if (params.totalAmount != null) this.entity.totalAmount = params.totalAmount;
    if (params.totalAmountVatIncluded != null)
      this.entity.totalAmountVatIncluded = params.totalAmountVatIncluded;
    if (params.totalGain != null) this.entity.totalGain = params.totalGain;
    if (params.totalWeight != null) this.entity.totalWeight = params.totalWeight;
    if (params.totalWeightWithoutOptional != null)
      this.entity.totalWeightWithoutOptional = params.totalWeightWithoutOptional;
  }

  protected override async afterGetEntity(response: TDocDto) {
    super.afterGetEntity(response);
    await Promise.all([
      this.setLanguageCode(),
      this.loadTextTemplates(),
      this.loadPointOfSales(this.entity.thirdPartyId),
      this.loadWorkingSites(),
      this.fetchAccountingData(this.entity.id, this.entity.thirdPartyId),
      this.getThirdPartyLinkedDocCount(this.entity.thirdPartyId),
      this.resetDeliveryAddressForm({ emitEvent: false }),
      this.fetchThirdParty(this.entity.thirdPartyId, false),
    ]);
  }

  protected setLanguageCode() {
    let languageId = this.entity.languageId;
    this.languageCode = this.languageStore.getTranslatedLanguageCodeByLanguageId()(languageId);
  }

  protected updateThirdPartyService(thirdPartyRole: ThirdPartyRole) {
    this.thirdPartyService = (thirdPartyRole == ThirdPartyRole.Lead
      ? this.leadService
      : this.customerService) as unknown as TThirdPartyService;
    this.thirdPartyCache.clear();
    if (this.thirdPartyService != null) this.thirdPartyService$.next(this.thirdPartyService);
  }

  protected override initFormObservers(): void {
    super.initFormObservers();
    this.subscriptionService.addOne(
      this.form.get('thirdPartyId').valueChanges,
      this.onThirdPartyChanged.bind(this),
    );

    this.subscriptionService.addOne(
      this.form.get('deliveryAddress').get('thirdPartyAddressId').valueChanges,
      value => {
        this.deliveryAddressId$.next(value);

        this.loadContactDetails(this.thirdPartyId$.value, this.thirdPartyContact$.value, value);
      },
    );
  }

  protected getThirdPartyLinkedDocCount(thirdPartyId: string): void {
    this.thirdPartyService.getDocLinkedCount(thirdPartyId).subscribe(res => {
      this.thirdPartyDocLinkedCount = res;
    });
  }

  protected resetDeliveryAddressForm(options?: Object) {
    const deliveryAddressForm = this.form.get('deliveryAddress');

    deliveryAddressForm
      .get('thirdPartyAddressId')
      .setValue(this.entity.deliveryAddress_ThirdPartyAddressId, options);
    deliveryAddressForm.get('name').setValue(this.entity.deliveryAddress_Name, options);
    deliveryAddressForm.get('street').setValue(this.entity.deliveryAddress_Address1, options);
    deliveryAddressForm.get('localityId').setValue(this.entity.deliveryAddress_LocalityId, options);
    deliveryAddressForm.get('countryId').setValue(this.entity.deliveryAddress_CountryId, options);
    deliveryAddressForm.get('address2').setValue(this.entity.deliveryAddress_Address2, options);
    deliveryAddressForm
      .get('contactInfo')
      .setValue(this.entity.deliveryAddress_ContactInfo, options);
    deliveryAddressForm.get('latitude').setValue(this.entity.deliveryAddress_Latitude, options);
    deliveryAddressForm.get('longitude').setValue(this.entity.deliveryAddress_Longitude, options);
  }

  protected override afterSave(): void {
    super.afterSave();
    this.resetDeliveryAddressForm();
  }

  protected override setDefaultValue(response: ThirdPartyDocDefaultValueDto) {
    this.currentSalesPersonId = response.salesPersonId;
    this.entity = this.createEntity(Object.assign(this.defaultValue, response));

    if (response?.defaultThirdPartyId) {
      this.entity.thirdPartyId = response.defaultThirdPartyId;
    }

    this.resetDeliveryAddressForm();
    this.form.reset(this.entity);
    this.initBreadcrumb();
    this.isValidated.set(false);
    this.isClosed.set(false);
    this.isStockValidated.set(false);
    this.entitySignal.set(this.entity);
  }

  override saveRequest(context: DocSaveRequestContext): Observable<TDocDto> {
    const deliveryAddressFormValue = this.form.get('deliveryAddress').value;
    const formValue = this.form.getRawValue();

    formValue.deliveryAddress_ThirdPartyAddressId = deliveryAddressFormValue.thirdPartyAddressId;
    formValue.deliveryAddress_Name = deliveryAddressFormValue.name;
    formValue.deliveryAddress_Address1 = deliveryAddressFormValue.street;
    formValue.deliveryAddress_LocalityId = deliveryAddressFormValue.localityId;
    formValue.deliveryAddress_CountryId = deliveryAddressFormValue.countryId;
    formValue.deliveryAddress_Address2 = deliveryAddressFormValue.address2;
    formValue.deliveryAddress_ContactInfo = deliveryAddressFormValue.contactInfo;
    formValue.deliveryAddress_Latitude = deliveryAddressFormValue.latitude;
    formValue.deliveryAddress_Longitude = deliveryAddressFormValue.longitude;
    formValue.deliveryAddress_AddressFrom = this.thirdPartyDeliveryAddress()?.addressFrom;

    if (this.entity.id) {
      if (context?.recomputeVatCodes) {
        const canvaRef = this.modalService.open(DocVatCodeComputationDialogComponent, {
          ariaLabelledBy: 'modal-basic-title',
          centered: true,
          scrollable: true,
          injector: this.injector,
          size: 'l',
        });
        const componentInstance =
          canvaRef.componentInstance as DocVatCodeComputationDialogComponent;
        componentInstance.vatSystemId.set(this.thirdParty$.value?.vatSystem);

        return outputToObservable(componentInstance.vatCodeComputationConfirmed).pipe(
          concatMap(result => {
            if (result.allowComputation) {
              return this.docService.updateAndComputeVATCodes(
                this.entity.id,
                formValue,
                result.evaluateArticlePrices,
              );
            }
            return throwError(() => new Error('Doc save cancelled by user'));
          }),
        );
      } else {
        if (this.entity.thirdPartyId != this.form.get('thirdPartyId').value) {
          this.toastService.warn('Doc::CustomerChanged:VerifyPrices');
        }

        return this.docService.update(this.entity.id, formValue);
      }
    } else {
      return this.docService.create(formValue);
    }
  }

  protected override beforeSave(): Observable<DocSaveRequestContext> {
    return super.beforeSave().pipe(
      mergeMap(saveRequest => this.ensureThirdPartyLoadedBeforeSave().pipe(map(() => saveRequest))),
      mergeMap(saveRequest => {
        return this.validateVatSystem(saveRequest);
      }),
    );
  }

  protected validateVatSystem(
    saveRequest: DocSaveRequestContext,
  ): Observable<DocSaveRequestContext> {
    if (this.entity.id == null || this.entity.id === NewGuid) {
      saveRequest.recomputeVatCodes = false;
      return of(saveRequest);
    }

    return this.docService.validateVatSystem(this.entity.id, this.thirdPartyId$.value).pipe(
      map(isVatSystemValid => {
        saveRequest.recomputeVatCodes = !isVatSystemValid.isValid;
        return saveRequest;
      }),
    );
  }

  protected override getEntityCallback(): void {
    super.getEntityCallback();
    this.loadThirdPartyContacts(this.entity.thirdPartyId);
    this.loadThirdPartyAddresses();

    this.thirdPartyId$.next(this.entity.thirdPartyId);
    this.contactId$.next(this.entity.thirdPartyContactId);
    this.deliveryAddressId$.next(this.entity.deliveryAddress_ThirdPartyAddressId);
  }

  protected onThirdPartyChanged(thirdPartyId: string): void {
    if (thirdPartyId == null) return;

    this.fetchThirdParty(thirdPartyId);
    this.loadPointOfSales(thirdPartyId);
    this.loadWorkingSites();
    this.loadThirdPartyContacts(thirdPartyId);
    this.loadContactDetails(
      thirdPartyId,
      this.thirdPartyContact$.value,
      this.deliveryAddressId$.value,
    );

    this.updateAddressesFromThirdParty(thirdPartyId);
  }

  protected getFieldMappings(): FieldMapping[] {
    return [
      { formField: 'financialDiscountDays', customerField: 'financialDiscountDays' },
      { formField: 'financialDiscount', customerField: 'financialDiscount' },
      { formField: 'representativeId', customerField: 'representativeId' },
      { formField: 'paymentTermId', customerField: 'paymentTermId' },
      { formField: 'paymentMethodId', customerField: 'paymentMethodId' },
      { formField: 'invoiceTermId', customerField: 'invoiceTermId' },
    ];
  }

  protected updateAddressesFromThirdParty(thirdPartyId: string): void {
    this.subscriptionService.addOne(this.thirdPartyService.addressList(thirdPartyId), addresses => {
      this.filterThirdPartyAddresses(addresses);
      this.validateAndResetAddressFields(addresses);
      this.onThirdPartyAddressesUpdated(addresses);
    });

    this.subscriptionService.addOne(this.thirdPartyContacts$, contacts => {
      this.validateAndResetContactFields(contacts);
    });

    this.subscriptionService.addOne(this.thirdPartyInvoicingAddress$, addresses => {
      this.validateAndResetInvoicingAddressFields(addresses);
    });
  }

  /** Extension point invoked after the third party's addresses are (re)loaded, e.g. following a customer change. */
  protected onThirdPartyAddressesUpdated(addresses: AddressDto[]): void {}

  private validateAndResetAddressFields(addresses: AddressDto[]): void {
    const deliveryAddressControl = this.form.get('deliveryAddress')?.get('thirdPartyAddressId');
    if (
      deliveryAddressControl?.value != null &&
      !addresses.some(x => x.id === deliveryAddressControl.value)
    ) {
      deliveryAddressControl.setValue(null);
    }
  }

  private validateAndResetContactFields(contacts: ContactDto[]): void {
    const contactControl = this.form.get('thirdPartyContactId');
    if (contactControl?.value != null && !contacts.some(x => x.id === contactControl.value)) {
      contactControl.setValue(null);
    }
  }

  private validateAndResetInvoicingAddressFields(addresses: AddressDto[]): void {
    const invoicingAddressControl = this.form.get('invoicingAddress_ThirdPartyAddressId');
    if (
      invoicingAddressControl?.value != null &&
      !addresses.some(x => x.id === invoicingAddressControl.value)
    ) {
      invoicingAddressControl.setValue(null);
    }
  }

  protected fetchThirdParty(thirdPartyId: string, applyMappings: boolean = true) {
    const thirdParty$ = this.getThirdPartyById(thirdPartyId);
    this.subscriptionService.addOne(thirdParty$, (thirdParty: CustomerDto | SupplierDto) => {
      if (thirdParty == null) {
        return;
      }

      if (applyMappings) {
        if (thirdParty.languageId != null) {
          this.form.get('languageId').setValue(thirdParty.languageId);
        } else {
          this.form.get('languageId').setValue(this.currentCompanyStore.languageId);
        }

        const salesPersonControl = this.form.get('salesPersonId');
        if (salesPersonControl != null && salesPersonControl?.value == null) {
          if (thirdParty.salesPersonId != null) {
            salesPersonControl.setValue(thirdParty.salesPersonId);
          } else {
            if (this.currentSalesPersonId == null) {
              this.docService
                .getSalesPersonDefaultValue()
                .subscribeOnce((salesPersonId: string) => {
                  this.currentSalesPersonId = salesPersonId;
                  salesPersonControl.setValue(this.currentSalesPersonId);
                });
            } else {
              salesPersonControl.setValue(this.currentSalesPersonId);
            }
          }
        }

        const commonFields = this.getFieldMappings();
        this.applyFieldMappings(thirdParty, commonFields);
        this.applyThirdPartyConfiguration(thirdParty);
      }

      this.thirdParty$.next(thirdParty);
    });
  }

  private getThirdPartyById(thirdPartyId: string): Observable<CustomerDto | SupplierDto> {
    if (thirdPartyId == null) {
      return of(null);
    }

    const cachedRequest = this.thirdPartyCache.get(thirdPartyId);
    if (cachedRequest) {
      return cachedRequest;
    }

    const request$ = this.thirdPartyService.get(thirdPartyId).pipe(shareReplay(1));
    this.thirdPartyCache.set(thirdPartyId, request$);
    return request$;
  }

  protected loadPointOfSales(thirdPartyId: string) {
    if (thirdPartyId != null)
      this.pointOfSales$ = this.pointOfSaleService.getPointOfSalesWithInternByDefault(
        thirdPartyId,
        true,
      );
  }

  protected loadWorkingSites() {
    this.workingSites$ = this.workingSiteService
      .getLookupWorkingSiteList({ onlyActive: true, ids: [], queryProperties: [], sorting: [] })
      .pipe(
        first(),
        map(x => x.items),
      );
  }

  protected loadThirdPartyContacts(thirdPartyId?: string) {
    const contacts$ =
      thirdPartyId != null
        ? this.thirdPartyService.contacts(thirdPartyId, ContactType.Person, true)
        : of([]);

    this.subscriptionService.addOne(contacts$, contacts => this.thirdPartyContacts$.next(contacts));
  }

  protected loadThirdPartyAddresses() {
    this.subscriptionService.addOne(
      this.thirdPartyService.addressList(this.entity.thirdPartyId),
      addresses => this.filterThirdPartyAddresses(addresses),
    );
  }

  protected loadContactDetails(
    thirdPartyId?: string,
    contactId?: string,
    deliveryAddressId?: string,
  ) {
    const contactDetails$ =
      thirdPartyId != null
        ? this.docService.getContactDetails(thirdPartyId, deliveryAddressId, contactId)
        : of(null);

    this.subscriptionService.addOne(contactDetails$, contactDetails => {
      if (contactDetails == null) return;

      this.contactEmail = contactDetails.email;
      this.contactPhone = contactDetails.phone;

      if (this.entity) {
        this.entity.deliveryAddress = contactDetails.deliveryAddress;
      }
    });
  }

  protected redirectToThirdParty(thirdPartyId: string = NewId, addContact: boolean = false) {
    let url = window.location.href.replace(this.router.url, '');

    if (addContact) {
      url +=
        this.thirdPartyService != null
          ? `/third-parties/${this.getThirdPartyPath()}/${thirdPartyId}?addContact=yes`
          : `/third-parties/customers/${thirdPartyId}?addContact=yes`;
    } else {
      url +=
        this.thirdPartyService != null
          ? `/third-parties/${this.getThirdPartyPath()}/${thirdPartyId}`
          : `/third-parties/customers/${thirdPartyId}`;
    }

    window.addEventListener('focus', this.initEventContactListener);
    window.open(url, '_blank');
  }

  private getThirdPartyPath() {
    let pathMappings = [
      { type: CustomerService, path: 'customers' },
      { type: SupplierService, path: 'suppliers' },
      { type: LeadService, path: 'leads' },
    ];

    return pathMappings.find(x => this.thirdPartyService instanceof x.type)?.path;
  }

  protected initEventContactListener = () => {
    let id = localStorage.getItem('contactId');
    if (id != null) {
      this.loadContact(id);
      localStorage.removeItem('contactId');
      window.removeEventListener('focus', this.initEventContactListener);
    }
  };

  private loadContact(contactId: string) {
    const thirdPartyId = this.form.get('thirdPartyId').value;
    if (thirdPartyId == null) {
      return;
    }

    // The third-party screen can add, edit and delete multiple contacts, so reload
    // the full list rather than merging a single contact into the stale one.
    this.thirdPartyService
      .contacts(thirdPartyId, ContactType.Person, true)
      .subscribeOnce(contacts => {
        this.thirdPartyContacts$.next(contacts);

        // Recover the contact selected on the third-party screen, but only if it
        // still exists in the refreshed list.
        if (contacts.some(x => x.id === contactId)) {
          this.form.get('thirdPartyContactId').setValue(contactId);
        }
      });
  }

  private ensureThirdPartyLoadedBeforeSave(): Observable<void> {
    const thirdPartyId = this.form?.get('thirdPartyId')?.value;
    if (thirdPartyId == null) {
      return of(void 0);
    }

    const currencyId = this.form.get('currencyId')?.value;
    if (currencyId != null) {
      return of(void 0);
    }

    const thirdParty = this.thirdParty$.value;
    if (thirdParty?.id == thirdPartyId && thirdParty.currencyId != null) {
      return of(void 0);
    }

    return this.getThirdPartyById(thirdPartyId).pipe(
      tap(loadedThirdParty => {
        if (loadedThirdParty == null) {
          return;
        }

        this.thirdParty$.next(loadedThirdParty);
      }),
      map(() => void 0),
    );
  }

  private applyFieldMappings(customer: CustomerDto | SupplierDto, mappings: FieldMapping[]): void {
    mappings.forEach(mapping => {
      const formControl = this.form.get(mapping.formField);
      if (formControl && customer[mapping.customerField] !== undefined) {
        let value = customer[mapping.customerField];

        if (mapping.transform) {
          value = mapping.transform(value);
        }

        formControl.setValue(value);
      }
    });
  }

  override async ngOnDestroy() {
    await super.ngOnDestroy();
    window.removeEventListener('focus', this.initEventContactListener);
  }

  protected applyThirdPartyConfiguration(thirdParty: ThirdPartyDto) {
    // Do nothing by default
  }

  protected thirdPartySelect = (dto: DwActiveLookupRequestDto<string>) => {
    dto.onlyActive = true;
    dto.sorting = [{ property: 'name' }];
    dto.queryProperties = ['code', 'name'];
    return this.thirdPartyService.getLookupList(dto);
  };

  protected updatePricingDetails($event: LinePriceDetails) {
    this.linePricingDetails = $event;
  }
}
